import {GroupingChooserModel} from '@xh/hoist/cmp/grouping';
import {HoistModel, managed} from '@xh/hoist/core';
import {genDisplayName} from '@xh/hoist/data';
import {action, observable, observableRef} from '@xh/hoist/mobx';
import {camelCase, cloneDeep, fromPairs, isEqual, isNil, toPairs, uniq} from 'lodash';
import {SpecAssistantModel} from '../../spec/assistant/SpecAssistantModel';
import {GridSpecModel} from '../GridSpecModel';
import {findParentGroup, GridSpecColumnChanges, GridSpecOp, GridSpecOptions} from '../GridSpecOps';
import {
    CELL_STYLE_VALUES,
    FieldCatalogEntry,
    FormatSpec,
    GridSpecColumn,
    GridSpecFilter,
    GridSpecSorter,
    StyleCondition,
    StylePart,
    StyleRule
} from '../GridSpecTypes';

export interface SelectOption {
    value: any;
    label: string;
}

/** Row rules are dispatched to the spec, cell rules belong to the column being edited. */
export type StyleRuleScope = 'row' | 'cell';

/** One style in the rule dialog - the value is null until the user picks one. */
export interface StyleEntry {
    part: StylePart;
    value: string;
}

/**
 * UI state for the grid configurator. It holds no grid configuration of its own: it reads the
 * spec from the GridSpecModel and turns every edit into ops dispatched with source 'manual' - the
 * same path the AI assistant uses.
 */
export class GridSpecConfiguratorModel extends HoistModel {
    readonly specModel: GridSpecModel;
    /** Owned by the ConfigurableGridModel - rendered as the first configurator section. */
    readonly assistantModel: SpecAssistantModel;
    /** Fits the grid's columns to their content now - provided by the ConfigurableGridModel. */
    readonly autosizeColumns: () => void;

    @managed groupingChooserModel: GroupingChooserModel;

    /** Column being edited in the column dialog, with its draft settings. */
    @observable accessor editField: string = null;
    @observableRef accessor columnDraft: GridSpecColumn = null;
    @observable accessor columnDraftGroupId: string = null;

    /** Style rule being edited in the rule dialog - `ruleIndex` is null for a new rule. */
    @observable accessor ruleScope: StyleRuleScope = null;
    @observable accessor ruleIndex: number = null;
    @observableRef accessor ruleCondition: StyleCondition = null;
    @observableRef accessor ruleStyles: StyleEntry[] = null;

    constructor(specModel: GridSpecModel, assistantModel: SpecAssistantModel, autosizeColumns: () => void) {
        super();
        this.specModel = specModel;
        this.assistantModel = assistantModel;
        this.autosizeColumns = autosizeColumns;

        const dimensions = this.dimensionFields;
        if (specModel.capabilities.grouping !== 'none' && dimensions.length) {
            this.groupingChooserModel = new GroupingChooserModel({
                dimensions: dimensions.map(f => ({name: f.name, displayName: f.label ?? genDisplayName(f.name)})),
                initialValue: specModel.spec.groupBy,
                allowEmpty: true,
                commitOnChange: true
            });

            // Keep the chooser and the spec in step - whichever side changed
            this.addReaction(
                {
                    track: () => this.groupingChooserModel.value,
                    run: groupBy => {
                        if (!isEqual(groupBy, specModel.spec.groupBy)) this.dispatch([{op: 'setGroupBy', groupBy}]);
                    }
                },
                {
                    track: () => specModel.spec.groupBy,
                    run: groupBy => {
                        if (!isEqual(groupBy, this.groupingChooserModel.value)) this.groupingChooserModel.setValue(groupBy);
                    }
                }
            );
        }
    }

    get spec() {
        return this.specModel.spec;
    }

    get dimensionFields(): FieldCatalogEntry[] {
        return this.specModel.catalog.filter(f => f.isDimension);
    }

    get fieldOptions(): SelectOption[] {
        return this.specModel.catalog.map(f => ({value: f.name, label: this.specModel.fieldLabel(f.name)}));
    }

    get columnOptions(): SelectOption[] {
        return this.specModel.leafColumns.map(c => ({value: c.field, label: this.specModel.fieldLabel(c.field)}));
    }

    /** Catalog fields that are not shown in the grid - for "Add column". */
    get addableFieldOptions(): SelectOption[] {
        const {specModel} = this;
        return specModel.catalog
            .filter(f => {
                const col = specModel.getColumn(f.name);
                return !col || col.hidden;
            })
            .map(f => ({value: f.name, label: specModel.fieldLabel(f.name)}));
    }

    get groupOptions(): SelectOption[] {
        return [{value: null, label: 'None'}, ...this.specModel.columnGroups.map(g => ({value: g.groupId, label: g.headerName}))];
    }

    //------------------------
    // Options
    //------------------------
    setOption<K extends keyof GridSpecOptions>(key: K, value: GridSpecOptions[K]) {
        this.dispatch([{op: 'setOptions', options: {[key]: value}}]);
    }

    //------------------------
    // Columns
    //------------------------
    addColumn(field: string) {
        if (!field) return;
        const entry = this.specModel.getField(field),
            column: GridSpecColumn = {field};
        if (entry?.type === 'number') column.format = {type: 'number', numberStyle: 'plain'};
        if (entry?.type === 'date') column.format = {type: 'date', dateStyle: 'date'};
        this.dispatch([{op: 'addColumn', column}], `Add ${this.specModel.fieldLabel(field)}`);
    }

    removeColumn(field: string) {
        this.dispatch([{op: 'removeColumn', field}], `Remove ${this.specModel.fieldLabel(field)}`);
    }

    toggleColumnHidden(field: string) {
        const col = this.specModel.getColumn(field);
        this.dispatch(
            [{op: 'updateColumn', field, changes: {hidden: col.hidden ? null : true}}],
            `${col.hidden ? 'Show' : 'Hide'} ${this.specModel.fieldLabel(field)}`
        );
    }

    moveColumn(field: string, toIndex: number) {
        this.dispatch([{op: 'moveColumn', field, toIndex}], `Move ${this.specModel.fieldLabel(field)}`);
    }

    //------------------------
    // Column dialog
    //------------------------
    @action
    openColumnDialog(field: string) {
        this.editField = field;
        this.columnDraft = cloneDeep(this.specModel.getColumn(field));
        this.columnDraftGroupId = findParentGroup(this.spec, field)?.groupId ?? null;
    }

    @action
    closeColumnDialog() {
        this.editField = null;
        this.columnDraft = null;
        this.columnDraftGroupId = null;
    }

    @action
    updateColumnDraft(changes: Partial<GridSpecColumn>) {
        this.columnDraft = {...this.columnDraft, ...changes};
    }

    @action
    updateFormatDraft(changes: Partial<FormatSpec>) {
        const format = {type: 'text', ...this.columnDraft.format, ...changes} as FormatSpec;
        this.columnDraft = {...this.columnDraft, format};
    }

    @action
    setColumnDraftGroupId(groupId: string) {
        this.columnDraftGroupId = groupId;
    }

    @action
    saveColumnDialog() {
        const {editField: field, columnDraft: draft, columnDraftGroupId: groupId, specModel} = this,
            orig = specModel.getColumn(field),
            changes: GridSpecColumnChanges = {},
            ops: GridSpecOp[] = [];

        uniq([...Object.keys(orig), ...Object.keys(draft)])
            .filter(k => k !== 'field' && k !== 'format')
            .forEach(k => {
                if (!isEqual(orig[k], draft[k])) changes[k] = isEmptyValue(draft[k]) ? null : draft[k];
            });

        // Replace the format as a whole - clear it first, as updateColumn merges formats
        if (!isEqual(orig.format, draft.format)) {
            ops.push({op: 'updateColumn', field, changes: {format: null}});
            if (draft.format) changes.format = draft.format;
        }
        if (Object.keys(changes).length) ops.push({op: 'updateColumn', field, changes});
        if ((findParentGroup(this.spec, field)?.groupId ?? null) !== groupId) ops.push({op: 'setColumnGroup', field, groupId});

        if (ops.length) this.dispatch(ops, `Edit ${specModel.fieldLabel(field)}`);
        this.closeColumnDialog();
    }

    //------------------------
    // Column groups
    //------------------------
    addColumnGroup(headerName: string, fields: string[] = []): string {
        if (!headerName) return null;
        const existing = new Set(this.specModel.columnGroups.map(g => g.groupId)),
            base = camelCase(headerName) || 'group';
        let groupId = base;
        for (let i = 2; existing.has(groupId) || this.specModel.getColumn(groupId); i++) groupId = `${base}${i}`;

        this.dispatch([{op: 'addColumnGroup', groupId, headerName, fields}], `Add group ${headerName}`);
        return groupId;
    }

    renameColumnGroup(groupId: string, headerName: string) {
        this.dispatch([{op: 'updateColumnGroup', groupId, changes: {headerName}}], 'Rename group', `group-name-${groupId}`);
    }

    removeColumnGroup(groupId: string) {
        this.dispatch([{op: 'removeColumnGroup', groupId}], 'Remove group');
    }

    //------------------------
    // Sort
    //------------------------
    addSorter() {
        const used = this.spec.sortBy.map(s => s.colId),
            colId = this.specModel.leafColumns.find(c => !used.includes(c.field))?.field;
        if (colId) this.setSortBy([...this.spec.sortBy, {colId, sort: 'asc'}]);
    }

    updateSorter(index: number, changes: Partial<GridSpecSorter>) {
        this.setSortBy(this.spec.sortBy.map((s, i) => (i === index ? {...s, ...changes} : s)));
    }

    removeSorter(index: number) {
        this.setSortBy(this.spec.sortBy.filter((_, i) => i !== index));
    }

    private setSortBy(sortBy: GridSpecSorter[]) {
        this.dispatch([{op: 'setSort', sortBy}]);
    }

    //------------------------
    // Filters
    //------------------------
    addFilter() {
        const field = this.specModel.catalog[0]?.name;
        this.setFilters([...this.spec.filters, {field, op: '=', value: ''}], 'Add filter');
    }

    updateFilter(index: number, changes: Partial<GridSpecFilter>) {
        // Typing a value coalesces into one undo step
        this.setFilters(
            this.spec.filters.map((f, i) => (i === index ? {...f, ...changes} : f)),
            'Edit filter',
            'value' in changes ? `filter-${index}` : null
        );
    }

    removeFilter(index: number) {
        this.setFilters(
            this.spec.filters.filter((_, i) => i !== index),
            'Remove filter'
        );
    }

    private setFilters(filters: GridSpecFilter[], label: string, coalesceKey: string = null) {
        this.dispatch([{op: 'setFilters', filters}], label, coalesceKey);
    }

    //------------------------
    // Style rules
    //------------------------
    rulesFor(scope: StyleRuleScope): StyleRule[] {
        return scope === 'row' ? this.spec.rowClassRules : (this.columnDraft?.cellClassRules ?? []);
    }

    @action
    openRuleDialog(scope: StyleRuleScope, index: number = null) {
        const rule = isNil(index) ? null : this.rulesFor(scope)[index];
        this.ruleScope = scope;
        this.ruleIndex = index;
        if (rule) {
            this.ruleCondition = {...rule.when};
            this.ruleStyles = toPairs(rule.style).map(([part, value]) => ({part: part as StylePart, value}));
        } else if (scope === 'cell') {
            this.ruleCondition = {field: this.editField, op: '<', value: 0};
            this.ruleStyles = [{part: 'color', value: 'negative'}];
        } else {
            this.ruleCondition = {field: this.specModel.leafColumns[0]?.field, op: '>', value: 0};
            this.ruleStyles = [{part: 'background', value: 'primary'}];
        }
    }

    @action
    closeRuleDialog() {
        this.ruleScope = null;
        this.ruleIndex = null;
        this.ruleCondition = null;
        this.ruleStyles = null;
    }

    @action
    updateRuleCondition(changes: Partial<StyleCondition>) {
        this.ruleCondition = {...this.ruleCondition, ...changes};
    }

    /** Style parts not yet used by the rule - each part can be set once. */
    get unusedStyleParts(): StylePart[] {
        const used = this.ruleStyles?.map(s => s.part) ?? [];
        return (Object.keys(CELL_STYLE_VALUES) as StylePart[]).filter(p => !used.includes(p));
    }

    @action
    addRuleStyle() {
        const part = this.unusedStyleParts[0];
        if (part) this.ruleStyles = [...this.ruleStyles, {part, value: null}];
    }

    @action
    updateRuleStyle(index: number, changes: Partial<StyleEntry>) {
        // A new part has its own values - drop the old one
        if (changes.part) changes = {value: null, ...changes};
        this.ruleStyles = this.ruleStyles.map((s, i) => (i === index ? {...s, ...changes} : s));
    }

    @action
    removeRuleStyle(index: number) {
        this.ruleStyles = this.ruleStyles.filter((_, i) => i !== index);
    }

    get canSaveRule(): boolean {
        return !!this.ruleCondition?.field && !!this.ruleCondition.op && this.ruleStyles.some(s => s.value);
    }

    @action
    saveRuleDialog() {
        const {ruleScope: scope, ruleIndex: index} = this,
            rule: StyleRule = {when: this.ruleCondition, style: fromPairs(this.ruleStyles.filter(s => s.value).map(s => [s.part, s.value]))},
            rules = isNil(index) ? [...this.rulesFor(scope), rule] : this.rulesFor(scope).map((r, i) => (i === index ? rule : r));

        if (scope === 'row') {
            this.setRowRules(rules, isNil(index) ? 'Add row style' : 'Edit row style');
        } else {
            this.updateColumnDraft({cellClassRules: rules});
        }
        this.closeRuleDialog();
    }

    removeRule(scope: StyleRuleScope, index: number) {
        const rules = this.rulesFor(scope).filter((_, i) => i !== index);
        if (scope === 'row') {
            this.setRowRules(rules, 'Remove row style');
        } else {
            this.updateColumnDraft({cellClassRules: rules});
        }
    }

    private setRowRules(rules: StyleRule[], label: string, coalesceKey: string = null) {
        this.dispatch([{op: 'setRowStyleRules', rules}], label, coalesceKey);
    }

    //------------------------
    // Implementation
    //------------------------
    private dispatch(ops: GridSpecOp[], label?: string, coalesceKey?: string) {
        return this.specModel.dispatch(ops, {source: 'manual', label, coalesceKey});
    }
}

function isEmptyValue(v: any): boolean {
    return isNil(v) || v === '' || v === false;
}
